import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Sparkles, ArrowLeft, Menu, X, Users, Loader2, Check
} from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../ui/select";
import Sidebar from "./Sidebar";
import LanguageSwitcher from "./LanguageSwitcher";
import { API } from "../../lib/api";
import { useLang } from "../../lib/LanguageContext";

const ModelForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    gender: 'female',
    age_range: '20-25',
    hair_color: 'black',
    hair_style: 'long',
    eye_color: 'brown',
    body_type: 'slim',
    ethnicity: 'mixed',
    style: 'glamorous',
    personality: '',
    is_mature: false,
    initial_images: 1
  });
  const [bodyRefFiles, setBodyRefFiles] = useState([]); // [{file, preview}] - pending body ref uploads

  const fetchModel = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/models/${id}`);
      setFormData(response.data);
      // Load existing body reference previews for edit mode
      const refs = response.data.body_references || [];
      if (refs.length > 0) {
        const previews = await Promise.all(
          refs.map(async (ref) => {
            try {
              const res = await axios.get(`${API}/body-references/${ref.id}/raw`, { responseType: 'arraybuffer' });
              const b64 = btoa(new Uint8Array(res.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));
              return { id: ref.id, filename: ref.filename, preview: `data:image/png;base64,${b64}`, existing: true };
            } catch { return { id: ref.id, filename: ref.filename, preview: null, existing: true }; }
          })
        );
        setBodyRefFiles(previews);
      }
    } catch (error) {
      toast.error(t('errorLoading'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    if (isEdit && id) {
      fetchModel();
    }
  }, [isEdit, id, fetchModel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/models/${id}`, formData);
        // Upload any new body references (non-existing ones)
        const newRefs = bodyRefFiles.filter(r => !r.existing && r.file);
        for (const ref of newRefs) {
          const fd = new FormData();
          fd.append('file', ref.file);
          await axios.post(`${API}/models/${id}/body-references`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        toast.success(t('modelUpdated'));
        navigate('/dashboard');
      } else {
        const response = await axios.post(`${API}/models`, formData);
        const newModelId = response.data.id;
        
        // Upload body references to the new model
        const pendingRefs = bodyRefFiles.filter(r => r.file);
        if (pendingRefs.length > 0) {
          for (const ref of pendingRefs) {
            const fd = new FormData();
            fd.append('file', ref.file);
            try {
              await axios.post(`${API}/models/${newModelId}/body-references`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } catch (err) {
              console.error('Body ref upload failed:', err);
            }
          }
          toast.success(`Model kreiran s ${pendingRefs.length} referenc${pendingRefs.length === 1 ? 'om' : 'ama'} tijela!`);
        } else if (formData.is_mature && formData.initial_images > 0) {
          toast.success(`Model kreiran! Generira se ${formData.initial_images} slika u pozadini...`);
        } else {
          toast.success(t('modelCreated'));
        }
        navigate(`/models/${newModelId}`);
        return;
      }
    } catch (error) {
      toast.error(isEdit ? "Greška pri ažuriranju" : "Greška pri kreiranju");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBodyRefAdd = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (bodyRefFiles.length >= 3) {
      toast.error("Maksimalno 3 referentne slike tijela");
      return;
    }
    const preview = URL.createObjectURL(file);
    setBodyRefFiles(prev => [...prev, { file, preview, filename: file.name, existing: false }]);
  };

  const handleBodyRefRemove = async (index) => {
    const ref = bodyRefFiles[index];
    if (ref.existing && ref.id && isEdit) {
      // Delete from server for existing refs in edit mode
      try {
        await axios.delete(`${API}/models/${id}/body-references/${ref.id}`);
      } catch (err) {
        toast.error("Greška pri brisanju reference");
        return;
      }
    }
    setBodyRefFiles(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="main-content">
        {/* Mobile Header */}
        <div className="flex items-center justify-between mb-8 md:hidden">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-text-secondary hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Sparkles className="w-7 h-7 text-primary" />
          <LanguageSwitcher />
        </div>

        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Natrag
        </Button>

        {/* Form Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">
            {isEdit ? t('editModel') : t('createModel')}
          </h1>
          <p className="text-text-secondary mt-1">
            {isEdit ? 'Ažurirajte karakteristike vašeg AI modela' : 'Definirajte karakteristike vašeg AI modela'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-w-2xl space-y-8" data-testid="model-form">
          {/* Basic Info */}
          <div className="glass rounded-2xl p-6 space-y-6">
            <h2 className="text-xl font-bold">Osnovne informacije</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">{t('modelName')} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="npr. Aurora"
                  className="mt-2 bg-surface-highlight border-border"
                  required
                  data-testid="model-name-input"
                />
              </div>

              <div>
                <Label htmlFor="description">{t('description')}</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Opišite vašeg AI modela..."
                  className="form-input mt-2 min-h-[100px] resize-none"
                  data-testid="model-description-input"
                />
              </div>
            </div>
          </div>

          {/* Appearance */}
          <div className="glass rounded-2xl p-6 space-y-6">
            <h2 className="text-xl font-bold">Izgled</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('gender')}</Label>
                <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                  <SelectTrigger className="mt-2 bg-surface-highlight border-border" data-testid="gender-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="female">Ženski</SelectItem>
                    <SelectItem value="male">Muški</SelectItem>
                    <SelectItem value="non-binary">Nebinarni</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Dob</Label>
                <Select value={formData.age_range} onValueChange={(v) => handleChange('age_range', v)}>
                  <SelectTrigger className="mt-2 bg-surface-highlight border-border" data-testid="age-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="18-20">18-20</SelectItem>
                    <SelectItem value="20-25">20-25</SelectItem>
                    <SelectItem value="25-30">25-30</SelectItem>
                    <SelectItem value="30-35">30-35</SelectItem>
                    <SelectItem value="35-40">35-40</SelectItem>
                    <SelectItem value="40+">40+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('hairColor')}</Label>
                <Select value={formData.hair_color} onValueChange={(v) => handleChange('hair_color', v)}>
                  <SelectTrigger className="mt-2 bg-surface-highlight border-border" data-testid="hair-color-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="black">Crna</SelectItem>
                    <SelectItem value="brown">Smeđa</SelectItem>
                    <SelectItem value="blonde">Plava</SelectItem>
                    <SelectItem value="red">Crvena</SelectItem>
                    <SelectItem value="auburn">Kestenjasta</SelectItem>
                    <SelectItem value="gray">Siva</SelectItem>
                    <SelectItem value="white">Bijela</SelectItem>
                    <SelectItem value="pink">Ružičasta</SelectItem>
                    <SelectItem value="blue">Plava (fantasy)</SelectItem>
                    <SelectItem value="purple">Ljubičasta</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('hairStyle')}</Label>
                <Select value={formData.hair_style} onValueChange={(v) => handleChange('hair_style', v)}>
                  <SelectTrigger className="mt-2 bg-surface-highlight border-border" data-testid="hair-style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="long">Duga</SelectItem>
                    <SelectItem value="medium">Srednja</SelectItem>
                    <SelectItem value="short">Kratka</SelectItem>
                    <SelectItem value="curly">Kovrčava</SelectItem>
                    <SelectItem value="wavy">Valovita</SelectItem>
                    <SelectItem value="straight">Ravna</SelectItem>
                    <SelectItem value="braided">Pletenice</SelectItem>
                    <SelectItem value="pixie">Pixie</SelectItem>
                    <SelectItem value="bob">Bob</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('eyeColor')}</Label>
                <Select value={formData.eye_color} onValueChange={(v) => handleChange('eye_color', v)}>
                  <SelectTrigger className="mt-2 bg-surface-highlight border-border" data-testid="eye-color-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brown">Smeđe</SelectItem>
                    <SelectItem value="blue">Plave</SelectItem>
                    <SelectItem value="green">Zelene</SelectItem>
                    <SelectItem value="hazel">Orahove</SelectItem>
                    <SelectItem value="gray">Sive</SelectItem>
                    <SelectItem value="amber">Jantarne</SelectItem>
                    <SelectItem value="violet">Ljubičaste</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('bodyType')}</Label>
                <Select value={formData.body_type} onValueChange={(v) => handleChange('body_type', v)}>
                  <SelectTrigger className="mt-2 bg-surface-highlight border-border" data-testid="body-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="slim">Vitko</SelectItem>
                    <SelectItem value="athletic">Atletsko</SelectItem>
                    <SelectItem value="curvy">Oblo</SelectItem>
                    <SelectItem value="muscular">Mišićavo</SelectItem>
                    <SelectItem value="petite">Sitno</SelectItem>
                    <SelectItem value="tall">Visoko</SelectItem>
                    <SelectItem value="average">Prosječno</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('ethnicity')}</Label>
                <Select value={formData.ethnicity} onValueChange={(v) => handleChange('ethnicity', v)}>
                  <SelectTrigger className="mt-2 bg-surface-highlight border-border" data-testid="ethnicity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="caucasian">Kavkaški</SelectItem>
                    <SelectItem value="asian">Azijski</SelectItem>
                    <SelectItem value="african">Afrički</SelectItem>
                    <SelectItem value="latino">Latino</SelectItem>
                    <SelectItem value="middle-eastern">Bliskoistočni</SelectItem>
                    <SelectItem value="indian">Indijski</SelectItem>
                    <SelectItem value="mixed">Miješani</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('style')}</Label>
                <Select value={formData.style} onValueChange={(v) => handleChange('style', v)}>
                  <SelectTrigger className="mt-2 bg-surface-highlight border-border" data-testid="style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="glamorous">Glamurozan</SelectItem>
                    <SelectItem value="casual">Ležeran</SelectItem>
                    <SelectItem value="edgy">Odvažan</SelectItem>
                    <SelectItem value="elegant">Elegantan</SelectItem>
                    <SelectItem value="sporty">Sportski</SelectItem>
                    <SelectItem value="bohemian">Boho</SelectItem>
                    <SelectItem value="gothic">Gotički</SelectItem>
                    <SelectItem value="cyberpunk">Cyberpunk</SelectItem>
                    <SelectItem value="vintage">Vintage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Personality */}
          <div className="glass rounded-2xl p-6 space-y-6">
            <h2 className="text-xl font-bold">Osobnost</h2>
            
            <div>
              <Label htmlFor="personality">{t('personality')}</Label>
              <textarea
                id="personality"
                value={formData.personality}
                onChange={(e) => handleChange('personality', e.target.value)}
                placeholder="npr. samouvjerena, misteriozna, zavodljiva..."
                className="form-input mt-2 min-h-[80px] resize-none"
                data-testid="personality-input"
              />
            </div>
          </div>

          {/* Body Reference Images */}
          <div className="glass rounded-2xl p-6 space-y-4">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-secondary" />
                Referenca tijela
              </h2>
              <p className="text-text-secondary text-sm mt-1">
                Uploadajte 1-3 slike osobe kao bazu za građu i tijelo. AI će generirati sličnu figuru ali s potpuno drugačijim licem.
              </p>
            </div>
            
            <div className="flex gap-3 flex-wrap">
              {bodyRefFiles.map((ref, index) => (
                <div key={ref.id || ref.filename || index} className="relative group w-24 h-32 rounded-xl overflow-hidden border-2 border-secondary/30">
                  {ref.preview ? (
                    <img 
                      src={ref.preview} 
                      alt={ref.filename || `Ref ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-surface flex items-center justify-center">
                      <Users className="w-6 h-6 text-text-secondary" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleBodyRefRemove(index)}
                    className="absolute top-1 right-1 p-1 bg-black/70 rounded-full text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-body-ref-${index}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 text-center">
                    <span className="text-[10px] text-white/80">Ref {index + 1}</span>
                  </div>
                </div>
              ))}
              
              {bodyRefFiles.length < 3 && (
                <label className="w-24 h-32 rounded-xl border-2 border-dashed border-border hover:border-secondary/50 cursor-pointer flex flex-col items-center justify-center transition-colors bg-surface-highlight/50"
                  data-testid="form-body-ref-upload"
                >
                  <Plus className="w-6 h-6 text-text-secondary" />
                  <span className="text-xs text-text-secondary mt-1">Dodaj sliku</span>
                  <span className="text-[10px] text-text-secondary">({bodyRefFiles.length}/3)</span>
                  <input type="file" accept="image/*" onChange={handleBodyRefAdd} className="hidden" />
                </label>
              )}
            </div>
            
            {bodyRefFiles.length > 0 && (
              <div className="p-3 bg-secondary/10 rounded-lg text-sm text-text-secondary border border-secondary/20">
                <span className="text-secondary font-medium">Napomena:</span> AI će koristiti ove slike samo za oblik tijela i građu. Lice generiranog modela bit će potpuno novo i drugačije.
              </div>
            )}
          </div>

          {/* Mature Content */}
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{t('matureContent')}</h2>
                <p className="text-text-secondary text-sm mt-1">
                  Omogućite generiranje sadržaja za odrasle
                </p>
              </div>
              <Switch 
                checked={formData.is_mature}
                onCheckedChange={(v) => handleChange('is_mature', v)}
                data-testid="mature-content-switch"
              />
            </div>
          </div>

          {/* Initial Images - only show for new models */}
          {!isEdit && formData.is_mature && (
            <div className="glass rounded-2xl p-6 space-y-4">
              <div>
                <h2 className="text-xl font-bold">{t('initialImages')}</h2>
                <p className="text-text-secondary text-sm mt-1">
                  Generiraj slike automatski pri kreiranju modela
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleChange('initial_images', 1)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.initial_images === 1 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="initial-images-1"
                >
                  <div className="text-2xl font-bold">1</div>
                  <div className="text-sm text-text-secondary">slika</div>
                  <div className="text-xs text-text-secondary mt-1">Front view</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleChange('initial_images', 5)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.initial_images === 5 
                      ? 'border-secondary bg-secondary/10' 
                      : 'border-border hover:border-secondary/50'
                  }`}
                  data-testid="initial-images-5"
                >
                  <div className="text-2xl font-bold gradient-text">5</div>
                  <div className="text-sm text-text-secondary">slika</div>
                  <div className="text-xs text-text-secondary mt-1">Različiti kutovi i poze</div>
                </button>
              </div>
              
              {formData.initial_images === 5 && (
                <div className="p-3 bg-surface-highlight rounded-lg text-sm text-text-secondary">
                  <span className="text-secondary font-medium">Uključeno:</span> Front view, Sitting, Lying, Back view, Kneeling
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-4">
            <Button 
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex-1 border-white/10"
              data-testid="cancel-btn"
            >
              Odustani
            </Button>
            <Button 
              type="submit"
              disabled={saving || !formData.name}
              className="flex-1 bg-gradient-to-r from-primary to-secondary hover:opacity-90 btn-glow"
              data-testid="save-model-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Spremanje...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  {isEdit ? t('save') : t('createModel')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModelForm;
